import {
  Divider,
  Group,
  NumberInput,
  Stack,
  Text,
  TextInput
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification, updateNotification } from "@mantine/notifications";
import { useAppSelector } from "app/hooks";
import {
  getNotificationByResultType,
  loadingInfoNotification,
  obju,
  TreatmentData
} from "beitary-shared";
import {
  BBox,
  BDateAndTimeInput,
  BDateInput,
  CancelButton,
  MoveBackButton,
  SaveButton
} from "components";
import { selectActiveConsultationTreatments } from "features/consultations/Consultations.slice";
import { useDBServices } from "hooks/useDBService/useDBService";
import { useSubmitState } from "hooks/useSubmitState";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AdminBundleTxFormValues, rules } from "./AdminBundleTxForm.rules";

interface TxFormProps {
  consultationId: string;
  disabled: boolean;
}

export const AdminBundleTxForm = ({
  consultationId,
  disabled,
}: TxFormProps) => {
  const [submitState, setSubmitState] = useSubmitState();
  const { t } = useTranslation();
  const txDBService =
    useDBServices().consultationsDBService.treatments(consultationId);

  const { bundleId } = useParams();
  const bundle = useAppSelector(selectActiveConsultationTreatments).find(
    (t) => t.id === bundleId
  );

  const products = useAppSelector(selectActiveConsultationTreatments).filter(
    (p) =>
      p.fixedPriceBundleId &&
      p.fixedPriceBundleId === bundle?.id &&
      p.fixedPriceBundleId !== p.id
  );

  const form = useForm<AdminBundleTxFormValues>({
    initialValues: bundle
      ? {
          ...bundle,
          products: products.map((p) => ({
            ...p,
          })),
        }
      : undefined,
    validate: rules,
  });

  if (!bundle) return <Text>{t("NOT_FOUND")}</Text>;

  const submit = async (values: AdminBundleTxFormValues) => {
    if (disabled || !bundle) return;

    const items: { id: string; data: Partial<TreatmentData> }[] = [
      {
        id: bundle.id,
        data: { ...values },
      },
    ];
    values.products.forEach((p) => {
      obju.removeUndefined(p);
      let newItem: { id: string; data: Partial<TreatmentData> } = {
        id: p.id,
        data: p,
      };
      items.push(newItem);
    });

    const notificationId = "submit-treatment";
    showNotification(
      loadingInfoNotification({
        id: notificationId,
        message: t("Waiting for server response"),
        title: t("UPDATE_TREATMENT"),
      })
    );
    setSubmitState("pending-response");

    const result = await txDBService.updateTreatments(items);
    if (result.type === "success") {
      setSubmitState("success");
    } else {
      setSubmitState("error");
    }
    updateNotification({
      ...getNotificationByResultType(result.type)({
        message: t(result.message),
      }),
      id: notificationId,
    });
  };

  return (
    <form onSubmit={form.onSubmit(submit, (errors) => console.log(errors))}>
      <Stack>
        <Group>
          <MoveBackButton />
        </Group>
        <BBox header={<Text weight={500}>{bundle.productSnapshot.name}</Text>}>
          <Stack p="xl" spacing="xl">
            <Group>
              <NumberInput
                disabled={disabled}
                label={t("QUANTITY")}
                precision={2}
                rightSection={
                  bundle.unit ? (
                    <Text size="xs" mr="xs">
                      {t(bundle.unit)}
                    </Text>
                  ) : undefined
                }
                {...form.getInputProps("qty")}
                onChange={(v) => {
                  if (v !== "") {
                    products.forEach((p, index) => {
                      form.setFieldValue(`products.${index}.qty`, p.qty * v);
                    });
                    form.setFieldValue("qty", v);
                  }
                }}
              />
            </Group>

            <div>
              <Text weight={500} mt="xl" mb="xs">
                {t("PRODUCTS")}
              </Text>
              <Divider />
            </div>

            {products.map((p, index) => (
              <BBox header={<Text color="cyan">{p.productSnapshot.name}</Text>}>
                <Stack p="xl">
                  <Group grow>
                    <NumberInput
                      disabled={disabled}
                      label={t("QUANTITY")}
                      precision={2}
                      rightSection={
                        p.unit ? (
                          <Text size="xs" mr="xs">
                            {t(p.unit)}
                          </Text>
                        ) : undefined
                      }
                      {...form.getInputProps(`products.${index}.qty`)}
                    />
                    {form.values.dateAdministred && (
                      <BDateAndTimeInput
                        disabled={disabled}
                        label={t("DATE_ADMINISTRED")}
                        {...form.getInputProps(
                          `products.${index}.dateAdministred`
                        )}
                      />
                    )}
                  </Group>
                  <Group grow>
                    <TextInput
                      label={t("SERIAL_NUMBER")}
                      {...form.getInputProps(`products.${index}.serialNumber`)}
                      min={1}
                    />
                    <TextInput
                      disabled={disabled}
                      label={t("LOT_NUMBER")}
                      {...form.getInputProps(`products.${index}.lotNumber`)}
                    />
                    <BDateInput
                      disabled={disabled}
                      label={t("LOT_EXPIRATION_DATE")}
                      {...form.getInputProps(
                        `products.${index}.lotExpirationDate`
                      )}
                    />
                    <TextInput
                      disabled={disabled}
                      label={t("MANUFACTURER")}
                      {...form.getInputProps( `products.${index}.manufacturer`)}
                    />
                  </Group>
                </Stack>
              </BBox>
            ))}
          </Stack>
        </BBox>

        <Group position="right">
          <CancelButton />
          <SaveButton
            disabled={disabled}
            state={submitState}
            canSave={form.isValid()}
          />
        </Group>
      </Stack>
    </form>
  );
};
